import React from "react";

import {
  Chip,
  ChipProps,
  Stack,
  StackProps,
  Typography,
  useTheme,
} from "@mui/material";

import { sectionLabelSx } from "src/shell/styles/themeUtils";

export interface TagChipsProps {
  /** Key-value tag pairs. Both keys and values must be strings. */
  tags: Record<string, string> | undefined | null;
  /** MUI Chip variant. Defaults to "outlined". */
  variant?: ChipProps["variant"];
  /** Chip size. Defaults to "small". */
  size?: ChipProps["size"];
  /** Maximum number of tags to display before truncating. */
  max?: number;
  /** Whether to show a section label above the chips. */
  showLabel?: boolean;
  /** Custom label text. Defaults to "Tags". */
  labelText?: string;
  /** Stack direction. Defaults to "row". */
  direction?: StackProps["direction"];
  /** Gap between chips. Defaults to 0.5. */
  gap?: number;
  /** Empty state message when tags is empty/null. */
  emptyMessage?: string;
}

/**
 * Renders key-value tags as MUI Chips.
 * Works with the Record<string, string> tag format used by
 * Organizations, Projects, and Stacks.
 */
export const TagChips: React.FC<TagChipsProps> = ({
  tags,
  variant = "outlined",
  size = "small",
  max,
  showLabel = false,
  labelText = "Tags",
  direction = "row",
  gap = 0.5,
  emptyMessage,
}) => {
  const theme = useTheme();

  if (!tags || Object.keys(tags).length === 0) {
    if (emptyMessage) {
      return (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      );
    }
    return null;
  }

  const entries = Object.entries(tags);
  const displayEntries = max ? entries.slice(0, max) : entries;
  const hasMore = max ? entries.length > max : false;

  return (
    <>
      {showLabel && (
        <Typography variant="subtitle2" sx={sectionLabelSx}>
          {labelText}
        </Typography>
      )}
      <Stack direction={direction} flexWrap="wrap" gap={gap}>
        {displayEntries.map(([key, value]) => (
          <Chip
            key={key}
            label={`${key}: ${value}`}
            variant={variant}
            size={size}
            sx={{ fontWeight: 500 }}
          />
        ))}
        {hasMore && (
          <Chip
            label={`+${entries.length - (max ?? 0)} more`}
            variant={variant}
            size={size}
            sx={{
              fontWeight: 500,
              color: theme.palette.text.secondary,
            }}
          />
        )}
      </Stack>
    </>
  );
};

export default TagChips;
